import {
  Check,
  Code2,
  Copy,
  ExternalLink,
  Eye,
  Link2,
  Monitor,
  RefreshCw,
  Smartphone,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useState } from "react";

import { cn } from "@/lib/utils";
import { composeSrcdoc } from "@domain/studio-app";

import { HighlightedCode } from "./HighlightedCode";

type Tab = "preview" | "code";
type Device = "desktop" | "mobile";

/** The minimal app surface the preview needs — satisfied structurally by the
 *  domain `StudioApp` (which the hook re-exports as `StudioAppDetail`). */
export interface AppArtifactViewApp {
  name: string;
  /** Entry file within `files`. */
  entry: string;
  files: Record<string, string>;
}

/**
 * Share controls, wired by the fetch container (`AppArtifactContainer`) or
 * the detail route from `useStudioShares` + the billing plan. The component
 * derives "shared" from `url` presence: strip + anchor only when the URL has
 * actually landed (honest pending, spec §4.4).
 */
export interface AppArtifactShare {
  /** Live public URL when shared; null while private (or publish pending). */
  url: string | null;
  /** Publish/unpublish in flight — disables the share actions. */
  isBusy?: boolean;
  onShare: () => void;
  onUnshare: () => void;
}

interface AppArtifactViewProps {
  app: AppArtifactViewApp;
  /** Compact toolbar for narrow hosts (the chat artifact panel). */
  compact?: boolean;
  /** Re-read the app's files (the refresh button also re-mounts the iframe). */
  onRefresh?: () => void;
  /** Share state + actions; omitted → no share affordances (peek surfaces). */
  share?: AppArtifactShare;
}

/**
 * The app preview — Studio's core surface. Preview/Code tabs, device frame,
 * refresh, open-in-tab, and the share action (open to every plan).
 *
 * The iframe is sandbox="allow-scripts" ONLY. Never add allow-same-origin:
 * agent-generated HTML would run same-origin with the app and could reach
 * cookies/APIs as the user. Open-in-tab is a real anchor to the cookie-less
 * public share origin when shared, and disabled otherwise — never
 * blob/about:blank/document.write for agent-generated HTML.
 */
export function AppArtifactView({ app, compact = false, onRefresh, share }: AppArtifactViewProps) {
  const [tab, setTab] = useState<Tab>("preview");
  const [device, setDevice] = useState<Device>("desktop");
  const [frameKey, setFrameKey] = useState(0);
  const [copied, setCopied] = useState(false);

  /** Entry source as the agent wrote it — the Code tab shows this raw. */
  const entryHtml = app.files[app.entry] ?? "";
  /** Preview document with same-app relative CSS/JS inlined (spec M13). */
  const srcdoc = composeSrcdoc({ files: app.files, entry: app.entry });

  const shareUrl = share?.url ?? null;
  const isShared = shareUrl !== null;

  const onShareClick = useCallback(() => {
    share?.onShare();
  }, [share]);

  const onCopy = useCallback(() => {
    if (!shareUrl) return;
    void navigator.clipboard?.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  }, [shareUrl]);

  const iconBtn =
    "flex h-9 w-9 items-center justify-center rounded-full text-home-ink/60 transition-colors hover:bg-black/5 hover:text-home-ink dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white";

  return (
    <div className="flex h-full min-h-0 flex-col bg-home-cream dark:bg-surface-dark">
      {/* Toolbar */}
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-black/8 px-3 py-2 dark:border-white/10">
        {/* Preview / Code */}
        <div className="inline-flex gap-0.5 rounded-full bg-black/5 p-0.5 dark:bg-white/10">
          {(
            [
              { id: "preview", label: "Preview", icon: Eye },
              { id: "code", label: "Code", icon: Code2 },
            ] as const
          ).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-body text-[12px] font-semibold transition-colors",
                tab === t.id
                  ? "bg-white text-home-ink shadow-sm dark:bg-surface-dark-card dark:text-white"
                  : "text-home-ink/55 dark:text-white/55",
              )}
            >
              <t.icon className="h-3.5 w-3.5" strokeWidth={2.2} />
              {!compact && t.label}
            </button>
          ))}
        </div>

        {/* Device */}
        <div className="inline-flex gap-0.5 rounded-full bg-black/5 p-0.5 dark:bg-white/10">
          {(
            [
              { id: "desktop", icon: Monitor, label: "Desktop" },
              { id: "mobile", icon: Smartphone, label: "Mobile" },
            ] as const
          ).map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => setDevice(d.id)}
              title={d.label}
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-full transition-colors",
                device === d.id
                  ? "bg-white text-home-ink shadow-sm dark:bg-surface-dark-card dark:text-white"
                  : "text-home-ink/55 dark:text-white/55",
              )}
            >
              <d.icon className="h-3.5 w-3.5" strokeWidth={2.2} />
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => {
            setFrameKey((k) => k + 1);
            onRefresh?.();
          }}
          title="Refresh preview"
          className={iconBtn}
        >
          <RefreshCw className="h-4 w-4" strokeWidth={2.2} />
        </button>
        {/* Open in tab: a real anchor to the public share origin when shared;
            disabled (with the why) when not — never a blob URL. */}
        {isShared ? (
          <a
            href={shareUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="Open in new tab"
            className={iconBtn}
          >
            <ExternalLink className="h-4 w-4" strokeWidth={2.2} />
          </a>
        ) : (
          <button
            type="button"
            disabled
            title="Share this app to open it in its own tab"
            className={cn(iconBtn, "cursor-not-allowed opacity-40")}
          >
            <ExternalLink className="h-4 w-4" strokeWidth={2.2} />
          </button>
        )}

        {share && !isShared && (
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={onShareClick}
              disabled={share.isBusy}
              className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full bg-home-yellow px-4 font-body text-[12px] font-bold text-home-ink shadow-[0_8px_20px_-10px_rgba(33,31,28,0.45)] transition-transform hover:scale-[1.03] active:scale-[0.97] disabled:opacity-60"
            >
              <Link2 className="h-3.5 w-3.5" strokeWidth={2.4} />
              Share
            </button>
          </div>
        )}
      </div>

      {/* Share URL reveal — only when the URL has actually landed */}
      <AnimatePresence>
        {share && isShared && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="shrink-0 overflow-hidden border-b border-black/8 dark:border-white/10"
          >
            <div className="flex items-center gap-2 bg-accent-green/[0.08] px-4 py-2.5">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-green/15">
                <Link2 className="h-3 w-3 text-accent-green" strokeWidth={2.6} />
              </span>
              <a
                href={shareUrl ?? "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="min-w-0 flex-1 truncate font-body text-[13px] font-semibold text-home-ink/80 dark:text-white/80"
              >
                {shareUrl}
              </a>
              <button
                type="button"
                onClick={onCopy}
                className="inline-flex items-center gap-1 rounded-full border border-black/10 px-2.5 py-1 font-body text-[11px] font-bold text-home-ink/70 transition-colors hover:bg-black/5 dark:border-white/15 dark:text-white/70 dark:hover:bg-white/10"
              >
                {copied ? <Check className="h-3 w-3 text-accent-green" strokeWidth={2.6} /> : <Copy className="h-3 w-3" strokeWidth={2.2} />}
                {copied ? "Copied" : "Copy"}
              </button>
              <button
                type="button"
                onClick={share.onUnshare}
                disabled={share.isBusy}
                className="font-body text-[11px] font-semibold text-home-ink/50 transition-colors hover:text-home-terracotta disabled:opacity-60 dark:text-white/50"
              >
                Unshare
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Body */}
      {tab === "code" ? (
        <div className="min-h-0 flex-1 overflow-auto bg-[#211f1c] p-5">
          {/* `dark` wrapper: the code surface is always dark, so the dark hljs
              token palette from index.css must apply even in light mode. */}
          <pre className="dark font-mono text-[12px] leading-relaxed whitespace-pre-wrap text-[#e8e2d6]">
            <HighlightedCode code={entryHtml} file={app.entry} />
          </pre>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 items-stretch justify-center overflow-hidden p-3">
          <motion.div
            key={device}
            initial={{ opacity: 0, scale: 0.985 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.18 }}
            className={cn(
              "overflow-hidden bg-white shadow-[0_20px_50px_-24px_rgba(33,31,28,0.45)]",
              device === "mobile"
                ? "my-auto h-[560px] max-h-full w-[300px] rounded-[36px] border-[10px] border-[#211f1c] dark:border-black"
                : "h-full w-full rounded-2xl border border-black/8 dark:border-white/10",
            )}
          >
            {/*
              SECURITY: sandbox="allow-scripts" only — never allow-same-origin
              (agent HTML would escape the sandbox on our origin). Production
              serves these off a cookie-less origin (the studio apex domain).
            */}
            <iframe
              key={frameKey}
              srcDoc={srcdoc}
              title={app.name}
              sandbox="allow-scripts"
              referrerPolicy="no-referrer"
              className="h-full w-full border-0"
            />
          </motion.div>
        </div>
      )}

    </div>
  );
}
